"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function FullServiceSpectrum() {
  const header = useRevealOnScroll<HTMLDivElement>();
  const services = [{
    index: "/01",
    title: "INTERIOR PRECISION PAINTING",
    description: "Transform your living spaces with our meticulous interior painting services. We focus on clean lines, premium finishes, and color harmony to elevate the atmosphere of every room in your home.",
    features: ["Detailed surface preparation", "Premium low-VOC paint selection", "Custom color consultation", "Clean, dust-free workspace"],
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/interior_painting_service.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/interior_painting_service.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/interior_painting_service.png-wvc-sizes",
      alt: "Professional interior residential painting"
    },
    imageLeft: true
  }, {
    index: "/02",
    title: "EXTERIOR PROTECTION & FINISH",
    description: "Protect and beautify your home's exterior with our durable, weather-resistant painting solutions. We ensure long-lasting results that withstand the elements while enhancing curb appeal.",
    features: ["Weather-resistant coating systems", "Expert siding and trim restoration", "Comprehensive power washing", "Long-term durability protocols"],
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/exterior_painting_service.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/exterior_painting_service.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/exterior_painting_service.png-wvc-sizes",
      alt: "Professional exterior residential painting"
    },
    imageLeft: false
  }, {
    index: "/03",
    title: "CABINET REFINISHING",
    description: "Revitalize your kitchen or bathroom without the cost of a full remodel. Our cabinet refinishing process delivers a factory-smooth, durable finish that breathes new life into your cabinetry.",
    features: ["Professional spray application", "Durable enamel topcoats", "Hardware removal and reinstallation", "Custom color matching"],
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/cabinet_refinishing_service.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/cabinet_refinishing_service.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/cabinet_refinishing_service.png-wvc-sizes",
      alt: "Professional cabinet refinishing service"
    },
    imageLeft: true
  }, {
    index: "/04",
    title: "COLOR & DESIGN CONSULTATION",
    description: "Not sure which shade fits your home? Our design experts help you navigate the vast world of color to find the perfect palette that reflects your personal style and architectural character.",
    features: ["Personalized color palette creation", "Lighting and space analysis", "Sample testing and verification", "Professional design guidance"],
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/color_consultation_service.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/color_consultation_service.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/color_consultation_service.png-wvc-sizes",
      alt: "Professional color and design consultation"
    },
    imageLeft: false
  }];
  return <section data-section-id="5317" id="full-service-spectrum" className="bg-background text-foreground pt-32 pb-24 md:pt-44 md:pb-40 px-6 md:px-10 lg:px-16 overflow-hidden">
      <div className="max-w-[1440px] mx-auto">
        {}
        <div ref={header.ref} className={`transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex items-center gap-3 mb-10">
            <span className="text-primary text-lg" style={{
            lineHeight: 1
          }}>
              ●
            </span>
            <span className="font-default text-xs uppercase tracking-[0.2em] text-muted-foreground" style={{
            fontWeight: 600
          }}>
              SERVICES / OUR CRAFT
            </span>
          </div>

          <h1 className="font-default font-normal uppercase leading-[0.9] tracking-tight" style={{
          fontSize: "clamp(64px, 11vw, 180px)"
        }}>
            <span className="block">EXPERT</span>
            <span className="block md:pl-[100px]">
              <span className="font-serif italic font-normal">BRUSHWORK.</span>
            </span>
          </h1>

          <div className="mt-12 md:mt-16 md:pl-[100px] max-w-[580px]">
            <p className="font-serif italic text-foreground/85 leading-snug" style={{
            fontSize: "clamp(18px, 1.6vw, 24px)"
          }}>
              Elite Brushstrokes Painting brings precision and artistry to every project, 
              transforming residential spaces into refined environments through 
              meticulous preparation and premium application.
            </p>
          </div>
        </div>

        {}
        <div className="mt-20 md:mt-28 space-y-16 md:space-y-20">
          {services.map((service, i) => {
          const reveal = useRevealOnScroll<HTMLDivElement>({
            threshold: 0.15
          });
          return <div data-index={i} key={i} ref={reveal.ref} className={`transition-all duration-1000 ease-out ${reveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
                <article className="grid grid-cols-1 lg:grid-cols-12 bg-card border border-border shadow-md min-h-[600px]" style={{
              backgroundColor: "var(--card)"
            }}>
                  {}
                  <div className={`lg:col-span-5 relative overflow-hidden aspect-[4/3] lg:aspect-auto ${service.imageLeft ? "lg:order-1" : "lg:order-2"}`}>
                    <img src={service.image.src} data-wvc-srcset={service.image.srcset} data-wvc-sizes={service.image.sizes} alt={service.image.alt} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-primary/10 mix-blend-multiply" />
                  </div>

                  {}
                  <div className={`lg:col-span-6 lg:col-start-${service.imageLeft ? "7" : "1"} relative p-8 md:p-12 lg:p-16 flex flex-col justify-center ${service.imageLeft ? "lg:order-2" : "lg:order-1"}`}>
                    {}
                    <div className="font-default font-bold text-primary leading-none mb-8 lg:absolute lg:-left-6 lg:top-12" style={{
                  fontSize: "clamp(56px, 6vw, 96px)"
                }}>
                      {service.index}
                    </div>

                    <h2 className="font-default font-medium uppercase tracking-tight leading-[1.05] text-foreground" style={{
                  fontSize: "clamp(28px, 3.5vw, 48px)"
                }}>
                      {service.title}
                    </h2>

                    {}
                    <div className="mt-8 mb-8 h-px w-20 bg-primary" />

                    <p className="font-default text-foreground/80 leading-relaxed max-w-[520px]" style={{
                  fontSize: "18px"
                }}>
                      {service.description}
                    </p>

                    {}
                    <ul className="mt-10 space-y-3">
                      {service.features.map((feature, fi) => <li data-index={fi} key={fi} className="flex items-start gap-3">
                          <span className="inline-block w-1.5 h-1.5 bg-primary mt-[7px] shrink-0" aria-hidden="true" />
                          <span className="font-default text-foreground/75 uppercase" style={{
                      fontSize: "14px",
                      letterSpacing: "0.16em",
                      fontWeight: 600
                    }}>
                            {feature}
                          </span>
                        </li>)}
                    </ul>

                    {}
                    <div className="mt-12">
                      <Button asChild variant="link" className="h-auto p-0 text-foreground hover:text-primary transition-colors">
                        <Link to="/GetAQuotePage" className="group inline-flex items-center gap-3 font-default uppercase" style={{
                      fontSize: "13px",
                      letterSpacing: "0.2em",
                      fontWeight: 700,
                      textDecorationColor: "var(--primary)",
                      textUnderlineOffset: "8px",
                      textDecorationThickness: "1px"
                    }}>
                          <span className="underline decoration-primary underline-offset-8">
                            REQUEST A FREE QUOTE
                          </span>
                          <ArrowRight className="w-4 h-4 text-primary transition-transform group-hover:translate-x-1" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </article>
              </div>;
        })}
        </div>
      </div>
    </section>;
}